import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute, { RoleRedirect } from "@/components/auth/ProtectedRoute";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Onboarding from "@/pages/client/Onboarding";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import Clients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import Plans from "@/pages/admin/Plans";
import Revenue from "@/pages/admin/Revenue";
import Usage from "@/pages/admin/Usage";
import AdminReports from "@/pages/admin/AdminReports";
import AutomationLogs from "@/pages/admin/AutomationLogs";
import AiPrompts from "@/pages/admin/AiPrompts";
import AdminSettings from "@/pages/admin/AdminSettings";
import ProspectAudit from "@/pages/admin/ProspectAudit";

import ClientDashboard from "@/pages/client/ClientDashboard";
import GoogleConnection from "@/pages/client/GoogleConnection";
import ProfileSEO from "@/pages/client/ProfileSEO";
import Posts from "@/pages/client/Posts";
import AiPostGenerator from "@/pages/client/AiPostGenerator";
import CalendarPage from "@/pages/client/CalendarPage";
import Reviews from "@/pages/client/Reviews";
import AiReplies from "@/pages/client/AiReplies";
import ReviewRequests from "@/pages/client/ReviewRequests";
import QRCodePage from "@/pages/client/QRCodePage";
import Campaigns from "@/pages/client/Campaigns";
import Leads from "@/pages/client/Leads";
import Competitors from "@/pages/client/Competitors";
import ClientReports from "@/pages/client/ClientReports";
import Team from "@/pages/client/Team";
import Billing from "@/pages/client/Billing";
import ClientSettings from "@/pages/client/ClientSettings";
import Notifications from "@/pages/client/Notifications";
import BackupRestore from "@/pages/BackupRestore";

import NotFound from "@/pages/errors/NotFound";
import Unauthorized from "@/pages/errors/Unauthorized";
import AccountSuspended from "@/pages/errors/AccountSuspended";
import SubscriptionExpired from "@/pages/errors/SubscriptionExpired";

const CLIENT_ROLES = ["client_admin", "staff", "viewer"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RoleRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute roles={["client_admin"]}><Onboarding /></ProtectedRoute>} />

          {/* Super Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="plans" element={<Plans />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="usage" element={<Usage />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="logs" element={<AutomationLogs />} />
            <Route path="ai-prompts" element={<AiPrompts />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="prospect-audit" element={<ProspectAudit />} />
            <Route path="backup" element={<BackupRestore />} />
          </Route>

          {/* Client */}
          <Route path="/app" element={<ProtectedRoute roles={CLIENT_ROLES}><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<ClientDashboard />} />
            <Route path="google" element={<GoogleConnection />} />
            <Route path="seo" element={<ProfileSEO />} />
            <Route path="posts" element={<Posts />} />
            <Route path="ai-post" element={<AiPostGenerator />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="ai-replies" element={<AiReplies />} />
            <Route path="review-requests" element={<ReviewRequests />} />
            <Route path="qr" element={<QRCodePage />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="leads" element={<Leads />} />
            <Route path="competitors" element={<Competitors />} />
            <Route path="reports" element={<ClientReports />} />
            <Route path="team" element={<Team />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<ClientSettings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="backup" element={<BackupRestore />} />
          </Route>

          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/account-suspended" element={<AccountSuspended />} />
          <Route path="/subscription-expired" element={<SubscriptionExpired />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
